import { inject, injectable } from 'inversify';
import CookieRepository from '@framework/browser/storage/CookieRepository';
import Container from '@framework/di/Container';
import { TYPES } from '@framework/config/DI/types';
import type { TNonce } from '@framework/router/Next/headers/ContentSecurityPolicyHeader/TNonce';

declare global {
  interface Window {
    FrontChat?: (command: string, options?: unknown) => void,
  }
}

type FrontChatStatus = 'idle' | 'loading' | 'ready' | 'error';

const SCRIPT_URL = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';

@injectable()
class FrontChatService {
  private status: FrontChatStatus = 'idle';

  private readyPromise: Promise<void> | null = null;

  constructor(
    @inject(Container) private readonly container: Container,
    @inject(CookieRepository) private readonly cookieRepository: CookieRepository,
  ) {}

  private setStatus(newStatus: FrontChatStatus): void {
    this.status = newStatus;
  }

  private load(chatId: string): Promise<void> {
    if (this.readyPromise) {
      return this.readyPromise;
    }

    this.setStatus('loading');

    this.readyPromise = new Promise<void>((resolve, reject) => {
      this.container.getAsync<TNonce>(TYPES.Nonce).then((nonce) => {
        const script = document.createElement('script');
        script.src = SCRIPT_URL;
        script.async = true;
        if (nonce) {
          script.setAttribute('nonce', nonce);
        }

        script.onload = () => {
          try {
            this.cookieRepository.removeFromCurrentDomain('fcpmuc');
            // @ts-ignore
            window.FrontChat('init', {
              chatId,
              useDefaultLauncher: false,
              onInitCompleted: () => {
                this.setStatus('ready');
                resolve();
              },
            });
          } catch (error) {
            this.setStatus('error');
            reject(error);
          }
        };

        script.onerror = () => {
          this.setStatus('error');
          reject(new Error('FrontChat script failed to load'));
        };

        document.body.appendChild(script);
      }).catch((error) => {
        this.setStatus('error');
        reject(error);
      });
    });

    return this.readyPromise;
  }

  async show(chatId: string): Promise<void> {
    await this.load(chatId);
    if (!window.FrontChat) {
      throw new Error('FrontChat is not available');
    }
    window.FrontChat('show');
  }

  getStatus(): FrontChatStatus {
    return this.status;
  }
}

export default FrontChatService;
